/**
 * Pure helpers for the PX Connect Korrespondenz attachment flow (UCPWO-378).
 *
 * Extracted from `usePxConnectKorrespondenz` so the failure handling is unit
 * testable in the node environment (no hooks). The flow generates PDFs
 * server-side, downloads each from its presigned URL, and re-uploads it into
 * the documents bucket. Every step here fails loudly: a requested PDF that
 * cannot be attached must abort the submit, never silently shrink it.
 */

/** Result shape of the PDF generators (usePdfs.getPdf / usePdf leakDetectionReport 'get'). */
export interface PdfResult {
    signedUrl?: string | null;
    error?: string | null;
}

export type UploadFn = (
    file: File
) => Promise<{ key?: string | null; name?: string | null }>;

/**
 * Assert a generated PDF is usable. Throws with a user-attributable message
 * when generation failed or returned no URL (e.g. the `pxConnectSchadenbericht`
 * Lambda template before its first deploy returns "Template not found").
 */
export const assertPdfResult = (result: PdfResult | undefined, label: string): string => {
    if (!result || result.error || !result.signedUrl) {
        throw new Error(
            `PDF "${label}" konnte nicht erzeugt werden${result?.error ? `: ${result.error}` : ''}`
        );
    }
    return result.signedUrl;
};

/**
 * Download a generated PDF from its presigned URL and re-upload it into the
 * documents bucket. Throws on HTTP errors and on upload failure (useAsset's
 * `upload` resolves with `{}` instead of throwing).
 */
export const reuploadPdf = async (
    upload: UploadFn,
    signedUrl: string,
    fileName: string,
    fetchImpl: typeof fetch = fetch
): Promise<{ key: string; name: string }> => {
    const response = await fetchImpl(signedUrl);
    if (!response.ok) {
        throw new Error(`PDF-Download fehlgeschlagen: ${response.status} ${response.statusText}`);
    }
    const blob = await response.blob();
    const file = new File([blob], fileName, { type: 'application/pdf' });
    const { key, name } = await upload(file);
    if (!key) {
        throw new Error(`PDF-Upload fehlgeschlagen: ${fileName}`);
    }
    return { key, name: name || fileName };
};
